import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export { API_URL };
export default api;

export const authApi = {
  login: (credentials: { email: string; password: string }) =>
    api.post('/login', credentials),
  register: (data: any) => api.post('/register', data),
  logout: () => api.post('/logout'),
  logoutAll: () => api.post('/logout-all'),
  me: () => api.get('/user'),
  updateProfile: (data: any) => api.put('/profile', data),
  updateAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('avatar', file);
    return api.post('/profile/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updatePassword: (data: { current_password: string; password: string; password_confirmation: string }) =>
    api.put('/password', data),
};

export const jobsApi = {
  list: (params?: any) => api.get('/jobs', { params }),
  get: (id: number) => api.get(`/jobs/${id}`),
  create: (data: any) => api.post('/jobs', data),
  update: (id: number, data: any) => api.put(`/jobs/${id}`, data),
  delete: (id: number) => api.delete(`/jobs/${id}`),
  myJobs: (params?: any) => api.get('/my-jobs', { params }),
  close: (id: number) => api.post(`/jobs/${id}/close`),
};

export const proposalsApi = {
  list: (params?: any) => api.get('/proposals', { params }),
  get: (id: number) => api.get(`/proposals/${id}`),
  create: (jobId: number, data: any) => api.post(`/jobs/${jobId}/proposals`, data),
  accept: (id: number) => api.post(`/proposals/${id}/accept`),
  reject: (id: number) => api.post(`/proposals/${id}/reject`),
  withdraw: (id: number) => api.post(`/proposals/${id}/withdraw`),
  myProposals: (params?: any) => api.get('/my-proposals', { params }),
};

export const categoriesApi = {
  list: (params?: any) => api.get('/categories', { params }),
  get: (id: number) => api.get(`/categories/${id}`),
};

export const conversationsApi = {
  list: (params?: any) => api.get('/conversations', { params }),
  get: (id: number) => api.get(`/conversations/${id}`),
  create: (data: { user_id: number; job_id?: number; message: string }) =>
    api.post('/conversations', data),
  sendMessage: (id: number, message: string) =>
    api.post(`/conversations/${id}/messages`, { message }),
};

export const reviewsApi = {
  list: (params?: any) => api.get('/reviews', { params }),
  create: (jobId: number, data: any) => api.post(`/jobs/${jobId}/reviews`, data),
  get: (id: number) => api.get(`/reviews/${id}`),
};

export const usersApi = {
  get: (id: number) => api.get(`/users/${id}`),
  workers: (params?: any) => api.get('/workers', { params }),
  toggleFavorite: (data: { favorable_id: number; favorable_type: string }) =>
    api.post('/favorites/toggle', data),
  myFavorites: (params?: any) => api.get('/favorites', { params }),
};

export const dashboardApi = {
  client: () => api.get('/dashboard/client'),
  worker: () => api.get('/dashboard/worker'),
};

export const workerProfileApi = {
  show: () => api.get('/worker/profile'),
  update: (data: any) => api.put('/worker/profile', data),
  uploadDocument: (type: 'dni_front' | 'dni_back' | 'selfie' | 'license_document', file: File) => {
    const formData = new FormData();
    formData.append('type', type);
    formData.append('file', file);
    return api.post('/worker/profile/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  documentPath: (type: string) => `/worker/profile/documents/${type}`,
  updateSkills: (skillIds: number[]) => api.put('/worker/profile/skills', { skill_ids: skillIds }),
  updateCategories: (categoryIds: number[]) => api.put('/worker/profile/categories', { category_ids: categoryIds }),
  acceptTerms: () => api.post('/worker/profile/accept-terms'),
  submit: () => api.post('/worker/profile/submit'),
};

export const portfoliosApi = {
  mine: () => api.get('/portfolios/mine'),
  create: (data: {
    title: string;
    description?: string;
    category_id?: number;
    link?: string;
    images: File[];
    authorized_publish: boolean;
  }) => {
    const formData = new FormData();
    formData.append('title', data.title);
    if (data.description) formData.append('description', data.description);
    if (data.category_id) formData.append('category_id', String(data.category_id));
    if (data.link) formData.append('link', data.link);
    formData.append('authorized_publish', data.authorized_publish ? '1' : '0');
    data.images.forEach((file) => formData.append('images[]', file));
    return api.post('/portfolios', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  update: (id: number, data: any) => api.put(`/portfolios/${id}`, data),
  delete: (id: number) => api.delete(`/portfolios/${id}`),
};

export const adminApi = {
  dashboard: () => api.get('/admin/dashboard'),
  users: (params?: any) => api.get('/admin/users', { params }),
  userDetail: (id: number) => api.get(`/admin/users/${id}`),
  documentPath: (userId: number, type: string) => `/admin/users/${userId}/documents/${type}`,
  updateUserStatus: (id: number, status: string) =>
    api.put(`/admin/users/${id}/status`, { status }),
  updateUserVerification: (id: number, data: { verification_status: 'approved' | 'rejected'; rejection_reason?: string }) =>
    api.put(`/admin/users/${id}/verification`, data),
  requestCorrection: (id: number, moderation_notes: string) =>
    api.post(`/admin/users/${id}/request-correction`, { moderation_notes }),
  toggleContactVerification: (id: number, field: 'phone' | 'email') =>
    api.post(`/admin/users/${id}/toggle-contact-verification`, { field }),
  jobs: (params?: any) => api.get('/admin/jobs', { params }),
  reports: (params?: any) => api.get('/admin/reports', { params }),
  resolveReport: (id: number, status: string) =>
    api.put(`/admin/reports/${id}`, { status }),
  banners: (params?: any) => api.get('/admin/banners', { params }),
  createBanner: (data: FormData) => api.post('/admin/banners', data),
  updateBanner: (id: number, data: any) => api.put(`/admin/banners/${id}`, data),
  deleteBanner: (id: number) => api.delete(`/admin/banners/${id}`),
  categories: () => api.get('/admin/categories'),
  createCategory: (data: any) => api.post('/admin/categories', data),
  updateCategory: (id: number, data: any) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id: number) => api.delete(`/admin/categories/${id}`),
};
